/**
 * Motion primitives — the whole set. Four components, no wrapper library.
 *
 * Why there is no motion dependency behind these: the app's motion budget is
 * a fade and a 3px lift. CSS does that natively, in zero bytes of JavaScript,
 * on the compositor, and — the part that matters for a tablet — without
 * re-rendering React to animate. A layout-animation library earns its weight
 * when elements genuinely travel between positions; nothing here does, and
 * per docs/ui-stable-loading.md nothing here is allowed to, because an
 * element travelling on load is the instability we are removing.
 *
 * Durations and easing come from the `--fc-dur-*` tokens in globals.css, so
 * `prefers-reduced-motion` is handled once, centrally, by collapsing those
 * tokens to 1ms. None of these components reads the media query itself and
 * none of them should: a primitive that checks it separately is a primitive
 * that can forget to.
 */
import type { ReactNode } from "react";
import { cn } from "@/lib/cn";

/**
 * Content arriving in space already reserved for it.
 *
 * Deliberately NOT a mount animation for anything that changes layout: if
 * this element's arrival makes its siblings move, the fix is to reserve the
 * space (see `<ReservedSlot>`), not to fade the movement in more gently.
 */
export function FadeIn({
  children,
  className,
  /** Stagger, in ms, for a short list arriving together. Keep it small and
   *  bounded — a long cascade turns a page load into a performance. */
  delay,
}: {
  children: ReactNode;
  className?: string;
  delay?: number;
}) {
  return (
    <div
      className={cn("fc-settle", className)}
      style={delay ? { animationDelay: `${delay}ms` } : undefined}
    >
      {children}
    </div>
  );
}

/**
 * Skeleton → content crossfade that cannot move anything.
 *
 * Both children occupy the SAME grid cell, so the container is always as tall
 * as the taller of the two and the swap is pure opacity. This is the reason
 * skeletons have to match the real component's geometry (phase 5 of the
 * stable-loading doc): when they do, "taller of the two" is one height and
 * the swap is invisible except as a fade. When they don't, this component
 * shows you exactly how badly — the box jumps by the difference.
 */
export function StablePresence({
  ready,
  skeleton,
  children,
  className,
}: {
  ready: boolean;
  skeleton: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("fc-swap", className)}>
      {/* aria-hidden + inert: a skeleton is decoration. It must never be
          read out, and once faded out it must not hold focus either. */}
      <div data-fc-state={ready ? "out" : "in"} aria-hidden inert={ready ? true : undefined}>
        {skeleton}
      </div>
      <div data-fc-state={ready ? "in" : "out"} aria-busy={!ready} inert={ready ? undefined : true}>
        {children}
      </div>
    </div>
  );
}

/**
 * Space that exists before its content does.
 *
 * The single most useful thing in this file. A header subtitle, an action
 * bar, a status strip — anything that resolves a beat after first paint gets
 * its final height reserved up front, and the page below it simply never
 * moves. `min-height` rather than `height` so content that turns out taller
 * than expected still renders in full instead of being clipped.
 */
export function ReservedSlot({
  minHeight,
  children,
  className,
}: {
  /** CSS length — the height the settled content needs. */
  minHeight: string;
  children?: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn("fc-slot", className)}
      style={{ "--fc-slot-h": minHeight } as React.CSSProperties}
    >
      {children}
    </div>
  );
}

/**
 * A size change the operator asked for — an expanding detail, an onboarding
 * step folding away once it is done.
 *
 * `grid-template-rows: 0fr → 1fr` animates to the content's real height
 * without anyone having to measure it, so this works on content that has not
 * loaded yet. Reach for it ONLY for operator-initiated changes: using it to
 * reveal arriving data is animating instability, which is the one thing the
 * stable-loading rules forbid.
 */
export function Collapse({
  open,
  children,
  className,
}: {
  open: boolean;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn("fc-collapse", className)}
      style={{ "--fc-rows": open ? "1fr" : "0fr" } as React.CSSProperties}
      aria-hidden={!open}
    >
      <div>{children}</div>
    </div>
  );
}
